// Pure readline edits for the Terminal's input line.
//
// Each helper takes `(text, caret)` and returns a whole new `{ text, caret }`.
// Returning the caret alongside the text is the point: an <input> does not
// move its caret when React swaps the value, so a kill that only returned text
// would leave the cursor wherever the browser last put it — usually the end —
// and the next keystroke would land in the wrong place. The caller sets both.
//
// The caret is clamped on the way in. It comes from `selectionStart`, which can
// be null or stale relative to the value React is about to render, and a caret
// past the end must not silently slice off the tail of the line.
//
// No imports: this runs in the browser bundle and under `node --test`
// unchanged.
//
// KEEP IN SYNC with ./shell-readline.d.mts (hand-written type mirror).

/**
 * @param {unknown} text
 * @param {unknown} caret
 * @returns {{ s: string, c: number }}
 */
function normalize(text, caret) {
  const s = typeof text === "string" ? text : "";
  const n = typeof caret === "number" && Number.isFinite(caret) ? Math.floor(caret) : s.length;
  return { s, c: Math.max(0, Math.min(s.length, n)) };
}

/**
 * Ctrl+U — delete from the start of the line to the caret.
 * @param {string} text
 * @param {number} caret
 * @returns {{ text: string, caret: number }}
 */
export function killToLineStart(text, caret) {
  const { s, c } = normalize(text, caret);
  return { text: s.slice(c), caret: 0 };
}

/**
 * Ctrl+K — delete from the caret to the end of the line.
 * @param {string} text
 * @param {number} caret
 * @returns {{ text: string, caret: number }}
 */
export function killToLineEnd(text, caret) {
  const { s, c } = normalize(text, caret);
  return { text: s.slice(0, c), caret: c };
}

/**
 * Ctrl+W — delete the word before the caret.
 *
 * Whitespace immediately left of the caret is skipped **first**, then the word
 * behind it is removed. Without that, `"ls foo  "` with the caret at the end
 * would delete the two spaces and leave `"ls foo"` — so the keystroke would
 * appear to do nothing useful and you'd have to press it twice for every word.
 *
 * Words are whitespace-delimited, matching bash's Ctrl+W (which is why a path
 * like `/a/b/c.txt` goes in one press rather than a component at a time).
 *
 * Text to the right of the caret is preserved.
 *
 * @param {string} text
 * @param {number} caret
 * @returns {{ text: string, caret: number }}
 */
export function killWordLeft(text, caret) {
  const { s, c } = normalize(text, caret);
  let i = c;
  while (i > 0 && /\s/.test(s[i - 1])) i--;
  while (i > 0 && !/\s/.test(s[i - 1])) i--;
  return { text: s.slice(0, i) + s.slice(c), caret: i };
}

/**
 * Resolve a history index to the line it should put on the prompt.
 *
 * `index === entries.length` is **the draft** — the position "newer than every
 * command", which is where the prompt sits before you press Up and where Down
 * returns you. It resolves to `""`; the caller restores the user's real
 * in-progress text there, because the draft is not part of history.
 *
 * Anything out of range, and any entry that isn't a string, is also the draft:
 * history is restored from localStorage, so a corrupt blob must degrade to an
 * empty prompt rather than render `undefined` into the input.
 *
 * @param {readonly unknown[] | undefined} entries
 * @param {number} index
 * @returns {string}
 */
export function historyAt(entries, index) {
  if (!Array.isArray(entries)) return "";
  if (!Number.isInteger(index) || index < 0 || index >= entries.length) return "";
  const v = entries[index];
  return typeof v === "string" ? v : "";
}
